import { useEffect, useMemo, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { Form } from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { Select } from "@/components/ui/select/select";
import { useInstallations } from "@/features/installations/use-installations";
import { GeneralModal } from "@/features/modals/general-modal";

import type { ShadowFloor, ShadowZone } from "../../types/location-map";

import { useCreateZone } from "../../hooks/useMapEntityCreates";
import { useNameBackedExternalIdSync } from "../../hooks/useNameBackedExternalId";
import { getRefId } from "../../utils/location/installation-ids";
import { floorsForInstallation } from "../../utils/location/zone-floor";
import { hasDuplicateExternalId } from "../../utils/shared/external-id";
import { createAtLocationDescription } from "./shared/create-at-location-description";
import { ZoneFloorSelect } from "./zone-floor-select";

const DUPLICATE_EXTERNAL_ID_MESSAGE =
	"A zone with this External ID already exists for this installation.";

export type CreateZoneFormProps = {
	floors?: ShadowFloor[];
	locationId: string;
	locationName?: string;
	onClose: () => void;
	onCreated?: (zoneId: string) => void;
	/** Existing zones at this location — used for client-side externalId uniqueness. */
	zones?: ShadowZone[];
};

type CreateZoneFormValues = {
	description: string;
	externalId: string;
	floorId: string;
	installation: string;
	name: string;
};

export const CreateZoneForm = ({
	floors = [],
	locationId,
	locationName,
	onClose,
	onCreated,
	zones = [],
}: CreateZoneFormProps) => {
	const createZone = useCreateZone();
	const { data: installations = [], isLoading: isLoadingInstallations } =
		useInstallations();
	const [submitError, setSubmitError] = useState<null | string>(null);

	const locationInstallations = useMemo(
		() =>
			installations.filter(
				(installation) => getRefId(installation.location) === locationId,
			),
		[installations, locationId],
	);

	const installationItems = useMemo(
		() =>
			locationInstallations.map((installation) => ({
				id: installation.id,
				label: installation.external.id,
			})),
		[locationInstallations],
	);

	const defaultValues = useMemo(
		(): CreateZoneFormValues => ({
			description: "",
			externalId: "",
			floorId: "",
			installation: locationInstallations[0]?.id ?? "",
			name: "",
		}),
		[locationInstallations],
	);

	const {
		control,
		formState: { errors, isValid },
		getValues,
		handleSubmit,
		setError,
		setValue,
		trigger,
	} = useForm<CreateZoneFormValues>({
		defaultValues,
		mode: "onChange",
	});

	const { syncExternalIdChange, syncNameChange } =
		useNameBackedExternalIdSync(setValue);

	const selectedInstallationId = useWatch({ control, name: "installation" });
	const installationFloors = useMemo(
		() => floorsForInstallation(floors, selectedInstallationId),
		[floors, selectedInstallationId],
	);

	// Keep installation default in sync once installations load.
	useEffect(() => {
		const current = getValues("installation");
		if (!current && locationInstallations[0]?.id) {
			setValue("installation", locationInstallations[0].id, {
				shouldValidate: true,
			});
		}
	}, [getValues, locationInstallations, setValue]);

	// Re-check externalId when the installation changes.
	useEffect(() => {
		if (!getValues("externalId").trim()) return;
		void trigger("externalId");
	}, [getValues, selectedInstallationId, trigger]);

	useEffect(() => {
		const currentFloorId = getValues("floorId");
		if (!currentFloorId) return;
		if (!installationFloors.some((floor) => floor.id === currentFloorId)) {
			setValue("floorId", "");
		}
	}, [getValues, installationFloors, setValue]);

	const validateExternalId = (value: string): string | true => {
		const trimmed = value.trim();
		if (!trimmed) return true;
		const installationId = getValues("installation").trim();
		if (hasDuplicateExternalId(trimmed, installationId, zones)) {
			return DUPLICATE_EXTERNAL_ID_MESSAGE;
		}
		return true;
	};

	const canSubmit =
		isValid && locationInstallations.length > 0 && !createZone.isPending;

	const onSubmit = handleSubmit((data) => {
		const installation = data.installation.trim();
		if (!installation) {
			setError("installation", { message: "Installation is required" });
			return;
		}

		if (hasDuplicateExternalId(data.externalId, installation, zones)) {
			setError("externalId", { message: DUPLICATE_EXTERNAL_ID_MESSAGE });
			return;
		}

		setSubmitError(null);
		createZone.mutate(
			{
				description: data.description.trim() || undefined,
				externalId: data.externalId.trim(),
				...(data.floorId ? { floor: data.floorId } : {}),
				installation,
				name: data.name.trim(),
			},
			{
				onError: () => {
					setSubmitError("Could not create zone.");
				},
				onSuccess: (created) => {
					onCreated?.(created.id);
					onClose();
				},
			},
		);
	});

	return (
		<GeneralModal
			closeOnSubmit={false}
			description={
				locationInstallations.length === 0
					? createAtLocationDescription(locationName, {
							fallback:
								"Create an installation for this location before adding a zone.",
							withLocation: (location) => (
								<>Create an installation for {location} before adding a zone.</>
							),
						})
					: createAtLocationDescription(locationName, {
							fallback: "Add a zone under an installation at this location.",
							withLocation: (location) => (
								<>Add a zone under an installation at {location}.</>
							),
						})
			}
			isOpen
			onClose={onClose}
			onSubmit={onSubmit}
			submitDisabled={!canSubmit}
			submitText={createZone.isPending ? "Creating…" : "Create zone"}
			title="Create zone"
		>
			<Form
				className="flex flex-col gap-4 pb-2"
				onSubmit={onSubmit}
				validationBehavior="aria"
			>
				<Controller
					control={control}
					name="name"
					render={({ field }) => (
						<Input
							errorMessage={errors.name?.message}
							isInvalid={Boolean(errors.name)}
							isRequired
							label="Name"
							onBlur={field.onBlur}
							onChange={(value) => syncNameChange(value, field.onChange)}
							placeholder="Lobby"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "Zone name is required",
						validate: (value) =>
							value.trim().length > 0 || "Zone name is required",
					}}
				/>
				<Controller
					control={control}
					name="externalId"
					render={({ field }) => (
						<Input
							errorMessage={errors.externalId?.message}
							hint="Auto-filled from the name; edit anytime."
							isInvalid={Boolean(errors.externalId)}
							isRequired
							label="External ID"
							onBlur={field.onBlur}
							onChange={(value) => {
								syncExternalIdChange(value, field.onChange);
								void trigger("externalId");
							}}
							placeholder="zone-lobby"
							ref={field.ref}
							value={field.value}
						/>
					)}
					rules={{
						required: "External ID is required",
						validate: validateExternalId,
					}}
				/>
				<Controller
					control={control}
					name="installation"
					render={({ field }) => (
						<Select
							errorMessage={errors.installation?.message}
							isDisabled={
								isLoadingInstallations || locationInstallations.length === 0
							}
							isInvalid={Boolean(errors.installation)}
							isRequired
							items={installationItems}
							label="Installation"
							onChange={(value) => {
								field.onChange(value ? String(value) : "");
							}}
							placeholder={
								isLoadingInstallations
									? "Loading installations…"
									: locationInstallations.length === 0
										? "No installations"
										: "Select installation"
							}
							value={field.value || null}
						>
							{(item) => (
								<Select.Item id={item.id} key={item.id}>
									{item.label}
								</Select.Item>
							)}
						</Select>
					)}
					rules={{ required: "Installation is required" }}
				/>
				<Controller
					control={control}
					name="floorId"
					render={({ field }) => (
						<ZoneFloorSelect
							floors={installationFloors}
							isDisabled={!selectedInstallationId}
							onChange={field.onChange}
							value={field.value}
						/>
					)}
				/>
				<Controller
					control={control}
					name="description"
					render={({ field }) => (
						<Input
							label="Description (optional)"
							onBlur={field.onBlur}
							onChange={field.onChange}
							ref={field.ref}
							value={field.value}
						/>
					)}
				/>
				{submitError ? (
					<p className="text-sm text-error-primary">{submitError}</p>
				) : null}
			</Form>
		</GeneralModal>
	);
};
